"use client"

import { useCallback, useEffect, useId, useSyncExternalStore } from "react"
import type { SystemStatus } from "@/actions/system-status"
import type { SyncStatus } from "./types"

type FeedStatus = { status: SyncStatus; builtAt?: string }
const feeds = new Map<string, FeedStatus>()
const listeners = new Set<() => void>()
const priority: Record<SyncStatus, number> = { live: 0, updating: 1, connecting: 2, paused: 3, denied: 4 }
let snapshot: FeedStatus | null = null
export const getSyncStatusSnapshot = () => snapshot
const getServerSnapshot = () => null
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } }

/** Mounted feeds only: leaving a page (including Activity hiding it) clears its status. */
export function reportSyncStatus(id: string, feed: FeedStatus | null) {
  if (feed) feeds.set(id, feed)
  else feeds.delete(id)
  let next: FeedStatus | null = null
  for (const entry of feeds.values()) if (!next || priority[entry.status] > priority[next.status]) next = entry
  if (next?.status === snapshot?.status && next?.builtAt === snapshot?.builtAt) return
  snapshot = next
  for (const listener of listeners) listener()
}

export function useReportSyncStatus(status: SyncStatus | "off", builtAt?: string) {
  const id = useId()
  useEffect(() => () => reportSyncStatus(id, null), [id])
  useEffect(() => { reportSyncStatus(id, status === "off" ? null : { status, builtAt }) }, [id, status, builtAt])
}

export function useLiveSyncStatus(enabled = true) {
  // The overflow menu subscribes only while open; this never polls or opens another feed.
  const listen = useCallback((listener: () => void) => enabled ? subscribe(listener) : () => {}, [enabled])
  return syncSystemStatus(useSyncExternalStore(listen, getSyncStatusSnapshot, getServerSnapshot))
}

export function syncSystemStatus(feed: FeedStatus | null): SystemStatus | null {
  if (!feed) return null
  const label = { live: "Live", updating: "Updating…", connecting: "Connecting…", paused: "Updates paused", denied: "Access changed" }[feed.status]
  const saved = feed.status === "paused" && feed.builtAt
    ? new Intl.DateTimeFormat("en-CA", { timeZone: "America/Vancouver", hour: "2-digit", minute: "2-digit" }).format(new Date(feed.builtAt)) : null
  return { name: "Live updates", status: feed.status === "denied" ? "error" : feed.status === "paused" || feed.status === "connecting" ? "warning" : "ok", detail: saved ? `${label} · ${saved}` : label }
}
